import { useState } from 'react';
import { KeyRound, CheckCircle2 } from 'lucide-react';
import { api } from '../../api/client';

export default function ChangePassword() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (next.length < 8) {
      setError('Nytt passord må være minst 8 tegn');
      return;
    }
    if (next !== confirm) {
      setError('De to nye passordene er ulike');
      return;
    }
    if (next === current) {
      setError('Nytt passord må være forskjellig fra det gamle');
      return;
    }
    setLoading(true);
    try {
      await api.post('/api/admin/change-password', { current_password: current, new_password: next });
      setDone(true);
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Kunne ikke bytte passord');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="p-6 max-w-md">
      <h1 className="text-2xl font-semibold mb-6 flex items-center gap-2">
        <KeyRound size={24} className="text-brand" /> Bytt passord
      </h1>

      {done ? (
        <div className="bg-white rounded-lg shadow border border-slate-200 p-6">
          <div className="flex items-center gap-2 text-green-700 mb-2">
            <CheckCircle2 size={20} /> <span className="font-medium">Passordet er byttet</span>
          </div>
          <p className="text-sm text-slate-600">
            Andre aktive økter er logget ut. Du forblir innlogget her.
          </p>
          <button
            onClick={() => setDone(false)}
            className="mt-4 text-sm text-brand hover:underline"
          >
            Bytt igjen
          </button>
        </div>
      ) : (
        <form onSubmit={submit} className="bg-white rounded-lg shadow border border-slate-200 p-6 space-y-4">
          <Field label="Nåværende passord">
            <input
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="input"
              required
            />
          </Field>
          <Field label="Nytt passord (minst 8 tegn)">
            <input
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="input"
              required
            />
          </Field>
          <Field label="Gjenta nytt passord">
            <input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="input"
              required
            />
          </Field>

          {error && <div className="text-sm text-red-600">{error}</div>}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-brand hover:bg-brand-dark text-white rounded py-2 font-medium disabled:opacity-50"
          >
            {loading ? 'Bytter …' : 'Bytt passord'}
          </button>
        </form>
      )}

      <style>{`.input { width:100%; border:1px solid #cbd5e1; border-radius:4px; padding:8px 10px; }`}</style>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1">{label}</span>
      {children}
    </label>
  );
}
